import React, { useState, useEffect, useRef,useCallback } from 'react';
import CopyTexttoClipBrd from '../CopyTexttoClipBrd/CopyTexttoClipBrd';
import { Modal, Button, Overlay, Tooltip, Spinner } from 'react-bootstrap';
import "./_CommonConfiramation.scss";
import { TEXT_PATTERN, LENGTH_RANGE } from '../../base/constants';

function CommonConfiramation({ show, onClose, popupConfig, onCancel, onSubmit, onBack }) {
  const [hide, setHide] = useState(true);
  const [step, setStep] = useState(1);
  const [selectedTierId, setSelectedTierId] = useState('');
  const [denialMessage, setDenialMessage] = useState({ value: "", invalid: true, error: false, helpText: `0 / ${LENGTH_RANGE.MAX}`, touched: false });

  useEffect(() => {
    if (show) {
      setStep(1);
      setSelectedTierId('');
      setDenialMessage({ value: "", invalid: true, error: false, helpText: `0 / ${LENGTH_RANGE.MAX}`, touched: false });
    }
  }, [show]);

  const onChangeDenialReasonRef = useRef();
  onChangeDenialReasonRef.current = (e) => {
    const textArea = e.target;
    const rawValue = textArea.value ?? '';
    const trimmedLength = rawValue.trim().length;
    const inputLength = rawValue.length;
    setDenialMessage(prev => ({
      ...prev,
      invalid: trimmedLength < LENGTH_RANGE.MIN,
      value: rawValue,
      helpText: `${inputLength} / ${LENGTH_RANGE.MAX}`,
    }));
  };

  const textAreaRef = useRef();
  const selectRef = useRef();

  useEffect(() => {
    const denialReason = textAreaRef.current;
    if (!denialReason) return;
    const handler = (e) => onChangeDenialReasonRef.current(e);
    denialReason.addEventListener('hbr-input', handler);
    denialReason.addEventListener('input', handler);
    return () => {
      denialReason.removeEventListener('hbr-input', handler);
      denialReason.removeEventListener('input', handler);
    };
  });

  const onSelectChange = useCallback((e) => {
    setSelectedTierId(e.target.value);
  }, []);

  useEffect(() => {
    const select = selectRef.current;
    if (!select) return;
    select.addEventListener('hbr-change', onSelectChange);
    return () => select.removeEventListener('hbr-change', onSelectChange);
  });

  const defaultConfig = {
    header: "",
    message: "",
    okButtonLabel: "Ok",
    cancelbuttonLabel: "Cancel",
    showCancel: true,
    sentiment:"primary" //danger
  }

  function handleClose() {
    setStep(1);
    setSelectedTierId('');
    setDenialMessage({ value: "", invalid: true, error: false, helpText: `0 / ${LENGTH_RANGE.MAX}`, touched: false });
    if(onCancel) onCancel();
  }

  function buildSubmitValue() {
    let submitValue = "";
    if (popupConfig.provideDenialReason) {
      submitValue = denialMessage.value.trim();
    }
    if (popupConfig.provideSlaTierSelect) {
      submitValue = { newSlaTierId: String(selectedTierId), reason: submitValue };
    }
    return submitValue;
  }

  const isManualApprovalSelection = () => {
    if (!popupConfig.provideSlaTierSelect || !selectedTierId || !popupConfig.requestStatus) return false;
    const selectedTier = (popupConfig.slaTiers || []).find(t => String(t.id) === String(selectedTierId));
    return popupConfig.requestStatus.toLowerCase() === 'approved' && selectedTier?.autoApprove !== true;
  };

  const getConfirmationStep = () => {
    if (!popupConfig.confirmationStep) return null;
    if (popupConfig.manualApprovalConfirmationStep && isManualApprovalSelection()) {
      return popupConfig.manualApprovalConfirmationStep;
    }
    return popupConfig.confirmationStep;
  };

  function handleSubmit() {
    if (popupConfig.confirmationStep && step === 1) {
      setStep(2);
      return;
    }
    if(onSubmit) onSubmit(buildSubmitValue());
  }

  const isReasonRequired = popupConfig.reasonRequired !== false;

  const isSubmitDisabled = () => {
    if (popupConfig.slaTiersLoading) return true;
    if (popupConfig.provideDenialReason && isReasonRequired && denialMessage.invalid) return true;
    if (popupConfig.provideSlaTierSelect && !selectedTierId) return true;
    return false;
  };

  const handleView = () => {
    setHide(prev => !prev);
  }

  const excludeIds = new Set(
    (popupConfig.excludeTierIds || [popupConfig.currentTierId]).map(String)
  );
  const availableTiers = popupConfig.provideSlaTierSelect
    ? (popupConfig.slaTiers || []).filter(t => !excludeIds.has(String(t.id)))
    : [];

  if (popupConfig.confirmationStep && step === 2) {
    const confirmStep = getConfirmationStep();
    return (
      <Modal show={show} centered className='app-modal-popup fixed-width' backdrop='static' onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>{confirmStep.header}</Modal.Title>
        </Modal.Header>
        <Modal.Body className='px-5 pt-4 common-modal'>
          <div className='px-1'>
            <div className='message'>
              {confirmStep.message}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant={confirmStep.cancelVariant ?? 'outline-secondary'} onClick={handleClose}>
            {confirmStep.cancelButtonLabel ?? popupConfig.cancelbuttonLabel ?? defaultConfig.cancelbuttonLabel}
          </Button>
          <Button variant={confirmStep.sentiment ?? 'primary'} onClick={() => { if(onSubmit) onSubmit(buildSubmitValue()); }}>
            {confirmStep.okButtonLabel}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
      <Modal show={show} centered className='app-modal-popup fixed-width' backdrop='static' onHide={handleClose}>
      <Modal.Header closeButton>
        {onBack && (
          <span className="back-arrow" role="button" onClick={onBack}>
            <i className="bi bi-chevron-left"></i>
          </span>
        )}
        <Modal.Title>{popupConfig.header}</Modal.Title>
      </Modal.Header>
      <Modal.Body className='px-5 pt-4 common-modal' centered>
      <div className='px-1'>
        <div className='message'>
          {popupConfig.message}
        </div>
          {popupConfig.currentTierDisplay && (
            <div className='mt-4 mb-4'>
              <strong>{popupConfig.tierLabel || 'Current SLA Tier'} :</strong>
              {'   '}{popupConfig.currentTierDisplay}
            </div>
          )}
          {popupConfig.provideSlaTierSelect && (
            <div className='mb-3'>
              {popupConfig.slaTiersLoading && (
                <div className="d-flex align-items-center text-muted mb-2 sla-tiers-loading" aria-live="polite">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <span>Loading SLA tiers…</span>
                </div>
              )}
              <hbr-select
                ref={selectRef}
                label="New SLA Tier"
                placeholder={popupConfig.slaTiersLoading ? 'Loading…' : 'Select'}
                clearable="false"
                required
                value={selectedTierId}
                disabled={popupConfig.slaTiersLoading ? 'true' : undefined}
              >
                {popupConfig.slaTiersLoading ? (
                  <hbr-option value="__loading__" disabled>Loading SLA tiers…</hbr-option>
                ) : availableTiers.length === 0 ? (
                  <hbr-option value="__none__" disabled>No other tiers available</hbr-option>
                ) : (
                  availableTiers.map((tier) => {
                    const limit = tier.limits?.[0];
                    const limitKeys = limit ? Object.keys(limit) : [];
                    const limitText = limit
                      ? `(${limitKeys[0]}: ${limit[limitKeys[0]]}, ${limitKeys[1]}: ${limit[limitKeys[1]]})`
                      : '';
                    return (
                      <hbr-option key={tier.id} value={tier.id}>
                        <span style={{fontSize: "16px"}}>{tier.name}</span><br /><span>{limitText}</span>
                      </hbr-option>
                    );
                  })
                )}
              </hbr-select>
            </div>
          )}
          {popupConfig.provideDenialReason && (
            <div className='mt-3'>
              <hbr-textarea pattern={TEXT_PATTERN}
                placeholder="Type your message here"
                ref={textAreaRef} required={isReasonRequired} resize="none"
                maxlength={LENGTH_RANGE.MAX}
                minlength={isReasonRequired ? LENGTH_RANGE.MIN : 0}
                error={isReasonRequired && denialMessage.touched && denialMessage.invalid}
                onBlur={() => setDenialMessage({ ...denialMessage, touched: true })}
              >
                <div slot='label' className='denial_label'>{popupConfig.reasonLabel || 'Reason for Denial'}</div>
                <div slot='help-text' className='help-text d-flex justify-content-between'>
                  <span>
                    {isReasonRequired && denialMessage.invalid && denialMessage.touched && 'Please provide message with minimum 3 characters'}
                  </span>
                  <span>
                    {denialMessage.helpText}
                  </span>
                </div>
              </hbr-textarea>
            </div>
          )}
        {popupConfig?.data && popupConfig.data.client_id && (
          <>
            <div className="fw-bold head-txt mt-3">
              Client ID
            </div>
            <div className="d-flex mt-1 align-items-center">
              <div className="sub-txt">
                {popupConfig.data.client_id}
              </div>
              <div className="ms-auto">
                <CopyTexttoClipBrd textToCopy={popupConfig.data.client_id} />
              </div>
            </div>
              <div className="head-txt mt-3 fw-bold align-items-center">
                Client Secret
              </div>
            <div className="d-flex mt-1 align-items-center">
              <div className="sub-txt">
                <span className={hide ? 'secureText sub-txt' : 'sub-txt'}>{popupConfig.data.client_secret}</span>
              </div>
              <div className="ms-auto d-flex align-items-center">

                <i role="button" aria-label={hide ? 'Show secret' : 'Hide secret'} className={` ${hide ? 'eye' : 'eye-closed'} atm-icon eye-icon`} onClick={() => handleView()} />
                <div className="ms-auto">
                  <CopyTexttoClipBrd textToCopy={popupConfig.data.client_secret} />
                </div>

              </div>
            </div>
          </>
        )}
      </div>
      </Modal.Body>
      <Modal.Footer> 

        {(popupConfig.showCancel ?? defaultConfig.showCancel)&& (
          <Button variant={popupConfig.cancelVariant ?? "outline-secondary"} onClick={handleClose}>
          {popupConfig.cancelbuttonLabel ?? defaultConfig.cancelbuttonLabel}
        </Button>
      ) 
          }
        <Button variant={popupConfig.sentiment ?? defaultConfig.sentiment} onClick={handleSubmit}
          disabled={isSubmitDisabled()}>
          {popupConfig.okButtonLabel}
        </Button>
      </Modal.Footer>
    </Modal>
    // <hbr-modal class="atm-modal--small common-modal" open={showPopup} onHbr-hide={handleSubmit}>

    //   <div slot='label' className="modal-title">{popupConfig.header}
    //   </div>
    //   <div className='px-1 modal-body'>
    //     <div className='message'>
    //       {popupConfig.message}
    //     </div>
    //     {popupConfig?.data && (
    //       <>
    //         <div className="d-flex mt-3">
    //           <div className="fw-bold head-txt">
    //             Client ID
    //           </div>
    //           <div className="sub-txt">
    //             {popupConfig.data.client_id}
    //           </div>
    //           <div className="ms-auto">
    //             <CopyTexttoClipBrd textToCopy={popupConfig.data.client_id} />
    //           </div>
    //         </div>


    //         <div className="d-flex mt-3">
    //           <div className="head-txt fw-bold align-items-center">
    //             Client Secret
    //           </div>
    //           <div className="sub-txt">
    //             <span className={hide ? 'secureText sub-txt' : 'sub-txt'}>{popupConfig.data.client_secret}</span>
    //           </div>
    //           <div className="ms-auto d-flex align-items-center">

    //             <i class={` ${hide ? 'eye' : 'eye-closed'} atm-icon eye-icon`} onClick={() => handleView()} />
    //             <div className="ms-auto">
    //               <CopyTexttoClipBrd textToCopy={popupConfig.data.client_secret} />
    //             </div>

    //           </div>
    //         </div>
    //       </>
    //     )}
    //   </div>

    //   <div slot="footer">
    //     {popupConfig.showCancel && (
    //       <hbr-button noPadding="true" variant="outline" class="mr-2 me-2" onClick={handleClose} >{popupConfig.cancelbuttonLabel}</hbr-button>)
    //     }
    //     <hbr-button noPadding="true" sentiment={popupConfig.sentiment ?? ''} onClick={handleSubmit}> {popupConfig.okButtonLabel}</hbr-button>
    //   </div>
    // </hbr-modal>
  )

}




export default CommonConfiramation;